import { useCallback, useRef, useEffect } from 'react';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { useSelectionStore } from '../../stores/SelectionStore';
import { calculateCenterOfPoints } from './mouseEventUtils';
import { SelectedPoint, CopiedObject } from '../types/eventTypes';
import { useFetchCountedAssemblies } from '../../hooks/useFetchCountedAssemblies';

// At the top of the file, add these type declarations
declare global {
  interface Window {
    __DEBUG_PASTE?: boolean;
    __NO_PREVIEW_LOGGED?: boolean;
  }
}

export interface CopyPasteEventProps {
  cursorPositionRef: React.MutableRefObject<{ x: number; y: number }>;
  pastePositionRef: React.MutableRefObject<{ x: number; y: number } | null>;
  selectedPoints: SelectedPoint[];
  setCopiedObjects: (objects: CopiedObject[]) => void;
  setShowPastePreview: (show: boolean) => void;
  setPastePreviewOffset: (offset: { x: number; y: number }) => void;
  pastePreviewPoints: React.MutableRefObject<any[]>;
  assembliesList: any[];
  copiedObjects: CopiedObject[];
}

export const useCopyPasteEvents = ({
  cursorPositionRef,
  pastePositionRef,
  selectedPoints,
  setCopiedObjects,
  setShowPastePreview,
  setPastePreviewOffset,
  pastePreviewPoints,
  assembliesList,
  copiedObjects,
}: CopyPasteEventProps) => {
  // Initialize debug flags if not already set
  // Set to true only in development and when specifically debugging paste issues
  if (typeof window.__DEBUG_PASTE === 'undefined') {
    window.__DEBUG_PASTE = process.env.NODE_ENV === 'development' && false; // Set to true when debugging
    window.__NO_PREVIEW_LOGGED = false;
  }

  const { data: filteredCountedAssemblies } = useFetchCountedAssemblies();

  const { setHasCopiedItems } = useStore(useSelectionStore);

  // Add local reference to the offset for easier access in renderPastePreview
  const offsetRef = useRef(setPastePreviewOffset);

  // Update the reference whenever the offset changes
  useEffect(() => {
    offsetRef.current = setPastePreviewOffset;
  }, [setPastePreviewOffset]);

  /**
   * Activates the paste preview
   */
  const activatePastePreview = useCallback(
    (overrideObjects?: CopiedObject[]) => {
      // Prefer freshly provided copied objects to avoid stale previews
      let objectsToPreview =
        overrideObjects && overrideObjects.length > 0 ? overrideObjects : copiedObjects;

      if (overrideObjects && overrideObjects.length > 0) {
        // Keep local state in sync when an override is provided
        setCopiedObjects(overrideObjects);
        // Also mirror into the selection store so other listeners see the same data
        const selectionStore = useSelectionStore.getState();
        selectionStore.setCopiedObjects(overrideObjects);
      } else if (objectsToPreview.length === 0) {
        // Check global selection store for objects copied via Point Editor
        const selectionStore = useSelectionStore.getState();
        if (selectionStore.copiedObjects && selectionStore.copiedObjects.length > 0) {
          objectsToPreview = selectionStore.copiedObjects;
          // Update local state to match global state
          setCopiedObjects(objectsToPreview);
        }
      }

      if (objectsToPreview.length === 0) {
        return;
      }

      // Reset and store the full copied objects for preview rendering (not just points)
      // Clearing first prevents flashes of the previous preview
      pastePreviewPoints.current = [];
      pastePreviewPoints.current = objectsToPreview;

      // Extract all points from copied objects for center calculation
      const allPoints = objectsToPreview.flatMap(obj => obj.points);
      if (allPoints.length === 0) {
        return;
      }

      // Calculate center of points to position the preview correctly
      const center = calculateCenterOfPoints(allPoints);

      // Initialize the preview at the current cursor position
      if (cursorPositionRef.current) {
        // DO NOT store cursor position in pastePositionRef here
        // pastePositionRef should only be set when freezing the position
        // Remove this line: pastePositionRef.current = { ...cursorPositionRef.current };

        const offset = {
          x: cursorPositionRef.current.x - center.x,
          y: cursorPositionRef.current.y - center.y,
        };
        setPastePreviewOffset(offset);
      }

      // Clear any previously frozen position
      pastePositionRef.current = null;

      // Set a flag on the window object to indicate preview is active
      // This will help other components know when a preview is showing
      (window as any).__pastePreviewActive = true;
      // Ensure we're not in frozen state
      (window as any).__pastePositionFrozen = false;

      setShowPastePreview(true);
    },
    [
      copiedObjects,
      cursorPositionRef,
      pastePositionRef,
      pastePreviewPoints,
      setPastePreviewOffset,
      setShowPastePreview,
      setCopiedObjects,
    ]
  );

  /**
   * Handles copy operation
   */
  const handleCopy = useCallback(() => {
    // First check local selectedPoints, then fall back to global selection store
    let pointsToCopy = selectedPoints;

    if (pointsToCopy.length === 0) {
      // Check global selection store for points selected via Point Editor
      const selectionStore = useSelectionStore.getState();
      if (selectionStore.selectedPoints && selectionStore.selectedPoints.length > 0) {
        pointsToCopy = selectionStore.selectedPoints;
      }
    }

    if (pointsToCopy.length === 0) {
      return;
    }

    // Group selected points by assembly ID
    const pointsByAssembly = new Map();

    // Create a map of pointId to true for faster lookups
    const selectedPointIds = new Set(pointsToCopy.map(p => p.pointId));

    // Gather the points that need to be copied
    assembliesList.forEach(assembly => {
      if (!assembly.pointsList) return;

      // Get all selected points from this assembly
      const pointsToCopyFromAssembly = assembly.pointsList.filter((point: { pointid: string }) =>
        selectedPointIds.has(point.pointid)
      );

      if (pointsToCopyFromAssembly.length > 0) {
        pointsByAssembly.set(assembly.assemblyid, {
          points: pointsToCopyFromAssembly,
          assemblyProperties: {
            ...assembly,
            pointsList: undefined, // Don't include points here - they're stored separately
          },
        });
      }
    });

    // Convert to array for storage
    const copiedItems = Array.from(pointsByAssembly.values()).map(
      ({ points, assemblyProperties }) => ({
        points,
        assemblyProperties,
      })
    );

    // Store the copied items
    setCopiedObjects(copiedItems);

    // Also store them in the selection store for persistence
    const selectionStore = useSelectionStore.getState();
    selectionStore.setCopiedObjects(copiedItems);

    // Set flags to indicate we have copied items
    setHasCopiedItems(true);

    // Create a debug element to store copiedObjects info
    try {
      let debugElement = document.getElementById('copied-objects-debug');
      if (!debugElement) {
        debugElement = document.createElement('div');
        debugElement.id = 'copied-objects-debug';
        debugElement.style.display = 'none';
        document.body.appendChild(debugElement);
      }

      // Store the number of objects and total points
      debugElement.setAttribute(
        'data-copied-objects',
        JSON.stringify({
          objectCount: copiedItems.length,
          totalPoints: copiedItems.reduce((sum, obj) => sum + obj.points.length, 0),
          assemblyIds: Array.from(pointsByAssembly.keys()),
        })
      );
    } catch (err) {
      console.error('[COPY DEBUG] Error creating debug element:', err);
    }

    // Visual feedback - flash the copied points
    if (copiedItems.length > 0) {
      // Get all affected assemblies
      const assemblyIds = Array.from(pointsByAssembly.keys());

      // Create a Set for faster lookups
      const assemblyIdSet = new Set(assemblyIds);
      const selectedPointIds = new Set(pointsToCopy.map(sp => sp.pointId));

      // Flash effect - Step 1: Highlight with different color
      const highlightedAssemblies = assembliesList.map(assembly => {
        if (!assemblyIdSet.has(assembly.assemblyid)) {
          return assembly;
        }

        const updatedPoints = assembly.pointsList?.map((point: any) => {
          if (selectedPointIds.has(point.pointid)) {
            return {
              ...point,
              isselected: true,
              iscopied: true, // Will use this for special styling in SVG rendering
            };
          }
          return point;
        });

        return {
          ...assembly,
          pointsList: updatedPoints,
        };
      });

      if (highlightedAssemblies) {
        // Apply the temporary highlight
        useUnityBuildStore.setState(state => ({
          ...state,
          filteredCountedAssemblies: [
            {
              ...(Array.isArray(filteredCountedAssemblies)
                ? {}
                : filteredCountedAssemblies?.filteredAssemblies?.[0]),
              assembliesList: highlightedAssemblies,
            },
          ],
        }));

        // Flash effect - Step 2: Return to normal selection after delay
        setTimeout(() => {
          const currentAssemblies = Array.isArray(filteredCountedAssemblies)
            ? undefined
            : filteredCountedAssemblies?.filteredAssemblies?.flatMap(
                (group: any) => group.assembliesList || []
              );
          if (currentAssemblies) {
            const normalizedAssemblies = currentAssemblies.map((assembly: any) => {
              if (!assemblyIdSet.has(assembly.assemblyid)) {
                return assembly;
              }

              const updatedPoints = assembly.pointsList?.map((point: any) => {
                if (selectedPointIds.has(point.pointid)) {
                  return {
                    ...point,
                    isselected: true,
                    iscopied: false, // Remove special styling
                  };
                }
                return point;
              });

              return {
                ...assembly,
                pointsList: updatedPoints,
              };
            });

            useUnityBuildStore.setState(state => ({
              ...state,
              filteredCountedAssemblies: [
                {
                  ...(Array.isArray(filteredCountedAssemblies)
                    ? {}
                    : filteredCountedAssemblies?.filteredAssemblies?.[0]),
                  assembliesList: normalizedAssemblies,
                },
              ],
            }));
          }
        }, 500); // 500ms flash effect
      }
    }

    // After copying, activate the paste preview with the freshly copied items
    activatePastePreview(copiedItems);
  }, [
    selectedPoints,
    assembliesList,
    setHasCopiedItems,
    activatePastePreview,
    setCopiedObjects,
    filteredCountedAssemblies,
  ]);

  /**
   * Handles paste operation
   */
  const handlePaste = useCallback(
    (e?: CustomEvent) => {
      // Check for custom event details
      const details = e?.detail || {};
      const useFrozenPosition = details.useFrozenPosition;

      // Always use the snapshot cursor position if available
      // This ensures consistency with what the user saw in the preview
      const pastePosition = pastePositionRef.current || cursorPositionRef.current;

      if (copiedObjects.length === 0 || !pastePosition) {
        return;
      }

      // Get current copy mode from the selection store
      const copyMode = useSelectionStore.getState().copyMode || 'create';

      // Get current object ID
      const currentObjectId = useUnityBuildStore.getState().selectedObject?.id;

      // For extend mode, verify we're still  on the same document
      if (
        copyMode === 'extend' &&
        copiedObjects[0].assemblyProperties.objectid !== currentObjectId
      ) {
        setShowPastePreview(false);
        useSelectionStore.getState().clearCopiedItems();
        return;
      }

      // Apply flip transformation if needed (same as preview)
      const isFlipped = useSelectionStore.getState().isFlipped;
      let finalCopiedObjects = copiedObjects;

      if (isFlipped) {
        // Calculate center of all points
        const allPoints = copiedObjects.flatMap((obj: any) => obj.points);
        const centerX = allPoints.reduce((sum: number, p: any) => sum + p.x, 0) / allPoints.length;

        // Apply flip transformation to match the preview
        finalCopiedObjects = copiedObjects.map((obj: any) => ({
          ...obj,
          points: obj.points.map((point: any) => ({
            ...point,
            x: 2 * centerX - point.x, // Flip horizontally around center
          })),
        }));
      }

      // Calculate the center of all points (after flip transformation) to create the offset
      const allCopiedPoints = finalCopiedObjects.flatMap(obj => obj.points);
      const center = calculateCenterOfPoints(allCopiedPoints);

      // The offset to apply to all points
      const offsetX = pastePosition.x - center.x;
      const offsetY = pastePosition.y - center.y;

      // Instead of manipulating the stores directly, dispatch a custom event
      // with all the necessary information to complete the paste operation
      document.dispatchEvent(
        new CustomEvent('app:paste:action', {
          detail: {
            copyMode,
            copiedObjects: finalCopiedObjects, // Use transformed objects
            pastePosition,
            offset: { x: offsetX, y: offsetY },
          },
        })
      );

      // Only hide the preview if we're not using a frozen position
      // or if we explicitly want to hide it after pasting a frozen position
      if (!useFrozenPosition) {
        // Hide paste preview after initiating paste
        setShowPastePreview(false);

        // Clear the frozen position reference if we completed a normal paste
        pastePositionRef.current = null;

        // Clear temporary points array in Unity Build store
        const ubsStore = useUnityBuildStore.getState();
        ubsStore.setTemporaryPoints([]);

        // Note: Don't clear selections here - they should be cleared after paste completion
        // to maintain visual consistency during the frozen preview period
      }
    },
    [pastePositionRef, cursorPositionRef, copiedObjects, setShowPastePreview]
  );

  /**
   * Renders paste preview elements
   */
  const renderPastePreview = useCallback(() => {
    if (!pastePreviewPoints.current.length) {
      // Remove or limit the frequency of this log message
      // Only log in debug mode or once per session
      if (window.__DEBUG_PASTE && !window.__NO_PREVIEW_LOGGED) {
        window.__NO_PREVIEW_LOGGED = true;
      }
      return null;
    }

    // Reset the logged flag when we do have points to render
    window.__NO_PREVIEW_LOGGED = false;

    // Get flip state from selection store
    const isFlipped = useSelectionStore.getState().isFlipped;

    // Calculate center of points to determine the offset
    const allPoints = pastePreviewPoints.current.flatMap(obj => obj.points);
    const center = calculateCenterOfPoints(allPoints);

    // Apply flip transformation if needed
    let transformedPoints = pastePreviewPoints.current;
    if (isFlipped) {
      transformedPoints = pastePreviewPoints.current.map(obj => ({
        ...obj,
        points: obj.points.map((point: any) => ({
          ...point,
          x: 2 * center.x - point.x, // Flip horizontally around center
        })),
      }));
    }

    // Determine which position to use
    // If we have a stored pastePosition (from right-click), use that
    // Otherwise follow the cursor
    const usePosition = pastePositionRef.current || cursorPositionRef.current;

    // Calculate current offset
    const offset = {
      x: usePosition.x - center.x,
      y: usePosition.y - center.y,
    };

    // Store the transformed coordinates globally for paste action
    const transformedCoordinates = transformedPoints.map(obj => ({
      ...obj,
      points: obj.points.map((point: any) => ({
        ...point,
        x: point.x + offset.x,
        y: point.y + offset.y,
      })),
    }));
    (window as any).__transformedPasteCoordinates = transformedCoordinates;

    const previewElements: Array<{
      key: string;
      type: 'circle' | 'rect' | 'line' | 'polygon' | 'group';
      x?: number;
      y?: number;
      radius?: number;
      width?: number;
      height?: number;
      x1?: number;
      y1?: number;
      x2?: number;
      y2?: number;
      points?: string; // For polygon shapes
      children?: Array<{
        // For group elements (cross, plus)
        type: 'line';
        x1: number;
        y1: number;
        x2: number;
        y2: number;
        stroke: string;
        strokeWidth: number;
      }>;
      fill?: string;
      stroke?: string;
      strokeWidth?: number;
      strokeDasharray?: string;
      strokeOpacity?: number;
      isPreview: boolean;
      isPasteFrozen: boolean;
      pointType?: string; // Store original point type for reference
    }> = [];

    // Render each copied object with its points and lines
    transformedPoints.forEach((copiedObj, objIndex) => {
      const { points, assemblyProperties } = copiedObj;

      // Render points with proper styling from assembly properties
      points.forEach((point: any, pointIndex: number) => {
        const previewX = point.x + offset.x;
        const previewY = point.y + offset.y;

        // Use assembly properties for styling, with fallbacks
        const pointSize = assemblyProperties?.pointsize || 5;
        const pointType = assemblyProperties?.pointtype || 'circle';
        const pointFillColor = assemblyProperties?.pointfillcolor || 'red';
        const pointBorderColor = assemblyProperties?.pointbordercolor || 'black';

        // Handle all shape types
        switch (pointType) {
          case 'circle':
            previewElements.push({
              key: `paste-preview-point-${objIndex}-${pointIndex}`,
              type: 'circle',
              x: previewX,
              y: previewY,
              radius: pointSize,
              fill: pointFillColor,
              stroke: pointBorderColor,
              strokeWidth: 1.5,
              isPreview: true,
              isPasteFrozen: !!pastePositionRef.current,
              pointType,
            });
            break;
          case 'square':
          case 'rect':
            const halfSize = pointSize / 2;
            previewElements.push({
              key: `paste-preview-point-${objIndex}-${pointIndex}`,
              type: 'rect',
              x: previewX - halfSize,
              y: previewY - halfSize,
              width: pointSize,
              height: pointSize,
              fill: pointFillColor,
              stroke: pointBorderColor,
              strokeWidth: 1.5,
              isPreview: true,
              isPasteFrozen: !!pastePositionRef.current,
              pointType,
            });
            break;
          case 'triangle':
            const trianglePoints = `
              ${previewX},${previewY - pointSize / 2}
              ${previewX - pointSize / 2},${previewY + pointSize / 2}
              ${previewX + pointSize / 2},${previewY + pointSize / 2}
            `;
            previewElements.push({
              key: `paste-preview-point-${objIndex}-${pointIndex}`,
              type: 'polygon',
              points: trianglePoints,
              fill: pointFillColor,
              stroke: pointBorderColor,
              strokeWidth: 1.5,
              isPreview: true,
              isPasteFrozen: !!pastePositionRef.current,
              pointType,
            });
            break;
          case 'diamond':
            const diamondPoints = `
              ${previewX},${previewY - pointSize / 2}
              ${previewX + pointSize / 2},${previewY}
              ${previewX},${previewY + pointSize / 2}
              ${previewX - pointSize / 2},${previewY}
            `;
            previewElements.push({
              key: `paste-preview-point-${objIndex}-${pointIndex}`,
              type: 'polygon',
              points: diamondPoints,
              fill: pointFillColor,
              stroke: pointBorderColor,
              strokeWidth: 1.5,
              isPreview: true,
              isPasteFrozen: !!pastePositionRef.current,
              pointType,
            });
            break;
          case 'cross':
            const crossStrokeWidth = Math.max(1, Math.round(pointSize / 6));
            previewElements.push({
              key: `paste-preview-point-${objIndex}-${pointIndex}`,
              type: 'group',
              children: [
                {
                  type: 'line',
                  x1: previewX - pointSize / 2,
                  y1: previewY - pointSize / 2,
                  x2: previewX + pointSize / 2,
                  y2: previewY + pointSize / 2,
                  stroke: pointBorderColor,
                  strokeWidth: crossStrokeWidth,
                },
                {
                  type: 'line',
                  x1: previewX - pointSize / 2,
                  y1: previewY + pointSize / 2,
                  x2: previewX + pointSize / 2,
                  y2: previewY - pointSize / 2,
                  stroke: pointBorderColor,
                  strokeWidth: crossStrokeWidth,
                },
              ],
              isPreview: true,
              isPasteFrozen: !!pastePositionRef.current,
              pointType,
            });
            break;
          case 'plus':
            const plusStrokeWidth = Math.max(1, Math.round(pointSize / 6));
            previewElements.push({
              key: `paste-preview-point-${objIndex}-${pointIndex}`,
              type: 'group',
              children: [
                {
                  type: 'line',
                  x1: previewX - pointSize / 2,
                  y1: previewY,
                  x2: previewX + pointSize / 2,
                  y2: previewY,
                  stroke: pointBorderColor,
                  strokeWidth: plusStrokeWidth,
                },
                {
                  type: 'line',
                  x1: previewX,
                  y1: previewY - pointSize / 2,
                  x2: previewX,
                  y2: previewY + pointSize / 2,
                  stroke: pointBorderColor,
                  strokeWidth: plusStrokeWidth,
                },
              ],
              isPreview: true,
              isPasteFrozen: !!pastePositionRef.current,
              pointType,
            });
            break;
          case 'star':
            const rOuter = pointSize / 2;
            const rInner = rOuter / 2.5;
            const starPoints: string[] = [];
            for (let i = 0; i < 10; i++) {
              const angle = (Math.PI / 5) * i - Math.PI / 2;
              const r = i % 2 === 0 ? rOuter : rInner;
              starPoints.push(
                `${previewX + r * Math.cos(angle)},${previewY + r * Math.sin(angle)}`
              );
            }
            previewElements.push({
              key: `paste-preview-point-${objIndex}-${pointIndex}`,
              type: 'polygon',
              points: starPoints.join(' '),
              fill: pointFillColor,
              stroke: pointBorderColor,
              strokeWidth: 1.5,
              isPreview: true,
              isPasteFrozen: !!pastePositionRef.current,
              pointType,
            });
            break;
          case 'hexagon':
            const hexRadius = pointSize / 2;
            const hexPoints: string[] = [];
            for (let i = 0; i < 6; i++) {
              const angle = (Math.PI / 3) * i - Math.PI / 2;
              hexPoints.push(
                `${previewX + hexRadius * Math.cos(angle)},${previewY + hexRadius * Math.sin(angle)}`
              );
            }
            previewElements.push({
              key: `paste-preview-point-${objIndex}-${pointIndex}`,
              type: 'polygon',
              points: hexPoints.join(' '),
              fill: pointFillColor,
              stroke: pointBorderColor,
              strokeWidth: 1.5,
              isPreview: true,
              isPasteFrozen: !!pastePositionRef.current,
              pointType,
            });
            break;
          default:
            // Default to circle for unknown types
            previewElements.push({
              key: `paste-preview-point-${objIndex}-${pointIndex}`,
              type: 'circle',
              x: previewX,
              y: previewY,
              radius: pointSize,
              fill: pointFillColor,
              stroke: pointBorderColor,
              strokeWidth: 1.5,
              isPreview: true,
              isPasteFrozen: !!pastePositionRef.current,
              pointType: pointType || 'circle',
            });
        }
      });

      // Render lines between points with proper styling
      if (points.length > 1) {
        // Only draw lines for length and area assemblies, not for count assemblies
        if (
          assemblyProperties?.assemblymeasurementtype === 'length' ||
          assemblyProperties?.assemblymeasurementtype === 'area'
        ) {
          for (let i = 0; i < points.length - 1; i++) {
            const currentPoint = points[i];
            const nextPoint = points[i + 1];

            // For length assemblies, check if the line should be drawn based on allowtrailingline
            if (assemblyProperties?.assemblymeasurementtype === 'length') {
              // Skip drawing this line if allowtrailingline is false for the next point
              if (nextPoint.allowtrailingline === false) {
                continue;
              }
            }

            const lineStartX = currentPoint.x + offset.x;
            const lineStartY = currentPoint.y + offset.y;
            const lineEndX = nextPoint.x + offset.x;
            const lineEndY = nextPoint.y + offset.y;

            // Use assembly properties for line styling, with fallbacks
            const lineColor = assemblyProperties?.linecolor || 'red';
            const lineSize = assemblyProperties?.linesize || 2;
            const lineDashArray = assemblyProperties?.linedasharray || '';
            const lineOpacity = assemblyProperties?.fillopacity || 0.5;

            previewElements.push({
              key: `paste-preview-line-${objIndex}-${i}`,
              type: 'line',
              x1: lineStartX,
              y1: lineStartY,
              x2: lineEndX,
              y2: lineEndY,
              stroke: lineColor,
              strokeWidth: lineSize,
              strokeDasharray: lineDashArray,
              strokeOpacity: lineOpacity,
              isPreview: true,
              isPasteFrozen: !!pastePositionRef.current,
            });
          }

          // Render closing line for area measurements if applicable
          if (assemblyProperties?.assemblymeasurementtype === 'area' && points.length > 2) {
            const firstPoint = points[0];
            const lastPoint = points[points.length - 1];

            const lineStartX = lastPoint.x + offset.x;
            const lineStartY = lastPoint.y + offset.y;
            const lineEndX = firstPoint.x + offset.x;
            const lineEndY = firstPoint.y + offset.y;

            const lineColor = assemblyProperties?.linecolor || 'red';
            const lineSize = assemblyProperties?.linesize || 2;
            const lineDashArray = assemblyProperties?.linedasharray || '';
            const lineOpacity = assemblyProperties?.fillopacity || 0.5;

            previewElements.push({
              key: `paste-preview-line-${objIndex}-closing`,
              type: 'line',
              x1: lineStartX,
              y1: lineStartY,
              x2: lineEndX,
              y2: lineEndY,
              stroke: lineColor,
              strokeWidth: lineSize,
              strokeDasharray: lineDashArray,
              strokeOpacity: lineOpacity,
              isPreview: true,
              isPasteFrozen: !!pastePositionRef.current,
            });
          }
        }
      }
    });

    return previewElements;
  }, [pastePreviewPoints, cursorPositionRef, pastePositionRef]);

  return {
    handleCopy,
    handlePaste,
    activatePastePreview,
    renderPastePreview,
  };
};
